import { freshState, LEGACY_KEYS, normalize, sampleState } from "@hachi/core";
import type { GarageState } from "@hachi/core";
import { readJson, writeJson } from "./storage";
import { noLocalDb } from "./localdb";
import type { LocalDb } from "./localdb";
import type { KeyValue } from "./storage";
import type { SyncState } from "./sync";

/**
 * The garage on this device. IndexedDB is the main copy. Every save also writes the current
 * app's localStorage keys, so rolling back to the old build shows everything, including changes
 * not uploaded yet (plan §3, dual-write). Removed only in a later, separately approved PR.
 */

/** When a copy was last edited. */
const editedAt = (st: GarageState) => String(st.meta.updatedAt ?? "");

export interface LoadResult {
  state: GarageState;
  sync: SyncState;
  source: "local-db" | "old-app" | "new";
}

/** Reads the current app's saved garage. A corrupt copy is kept aside instead of being lost (`load`). */
export function readLegacy(kv: KeyValue, now: () => number = Date.now): GarageState | null {
  let raw: string | null = null;
  try { raw = kv.getItem(LEGACY_KEYS.state); } catch { return null; }
  if (!raw) return null;
  try {
    return normalize(JSON.parse(raw));
  } catch {
    try { kv.setItem(`${LEGACY_KEYS.state}-corrupt-${now()}`, raw); } catch { /* storage full: nothing more we can do */ }
    return null;
  }
}

/**
 * Picks which copy to open. The old app's copy wins only when there's nothing newer here: first
 * launch, or after a rollback where someone kept working in the old app before coming back.
 */
export function chooseCopy(local: { state: GarageState; sync: SyncState } | null, legacy: GarageState | null, legacySync: SyncState): LoadResult {
  if (local && !(legacy && editedAt(legacy) > editedAt(local.state))) return { ...local, source: "local-db" };
  if (legacy) return { state: legacy, sync: legacySync, source: "old-app" };
  return { state: sampleState(), sync: {}, source: "new" };
}

export interface GarageDeps {
  kv: KeyValue;
  db: LocalDb;
  now?: () => number;
}

export function createGarage({ kv, db, now = Date.now }: GarageDeps) {
  let state: GarageState = freshState();
  let sync: SyncState = {};
  let version = 0;
  let queue: Promise<unknown> = Promise.resolve();
  const listeners = new Set<() => void>();
  const notify = () => { version++; for (const l of listeners) l(); };

  /** One write at a time, in order. A failed write never blocks later ones: the old-format copy is already saved. */
  function enqueue(job: () => Promise<void>) {
    queue = queue.then(job).catch(() => undefined);
  }

  // Saved means at least one copy reaches the device: the old-format key or IndexedDB.
  let kvSaved = !kv.temporary;
  let dbSaved = db !== noLocalDb;

  function persist() {
    kvSaved = !kv.temporary && writeJson(kv, LEGACY_KEYS.state, state);
    const snapshot = state;
    enqueue(() => db.saveState(snapshot).then(
      () => { dbSaved = db !== noLocalDb; },
      (e: unknown) => { dbSaved = false; throw e; },
    ).finally(() => { if (!kvSaved && !dbSaved) notify(); }));
  }

  return {
    /** Loads the garage. Call once before opening the app. */
    async load(): Promise<LoadResult> {
      const local = await db.load().catch(() => null);
      const legacy = readLegacy(kv, now);
      const legacySync = readJson<SyncState>(kv, LEGACY_KEYS.sync) ?? {};
      const r = chooseCopy(local, legacy, legacySync);
      state = r.state;
      sync = r.sync;
      if (r.source === "new") persist(); // Same as today's first start: the sample garage is saved straight away.
      if (r.source === "old-app") {
        enqueue(() => db.saveState(state));
        enqueue(() => db.saveSync(sync));
      }
      notify();
      return r;
    },

    state: () => state,
    version: () => version,
    /** False when nothing can be saved in this browser (private window, storage full). */
    storageOk: () => kvSaved || dbSaved,
    subscribe(fn: () => void) { listeners.add(fn); return () => void listeners.delete(fn); },

    /** A person changed something: marks it edited and saves. */
    update(change: (st: GarageState) => GarageState) {
      const next = change(state);
      state = { ...next, meta: { ...next.meta, changedSinceBackup: true, updatedAt: new Date(now()).toISOString() } };
      persist();
      notify();
    },

    /** Sync brought changes: saves without marking them as this device's edits. */
    replace(next: GarageState) {
      state = next;
      persist();
      notify();
    },

    loadSync: () => sync,
    saveSync(s: SyncState) {
      sync = s;
      writeJson(kv, LEGACY_KEYS.sync, s);
      const copy = structuredClone(s);
      enqueue(() => db.saveSync(copy));
    },

    /** Signing out: removes this account's garage from this browser. It stays in the account. */
    clear() {
      const clean = sampleState();
      state = { ...clean, meta: { ...clean.meta, welcomed: false } };
      sync = {};
      writeJson(kv, LEGACY_KEYS.sync, null);
      writeJson(kv, LEGACY_KEYS.restorePoints, null);
      writeJson(kv, LEGACY_KEYS.state, state);
      enqueue(() => db.clear());
      notify();
    },

    /** Resolves when everything queued is on disk. */
    flushed: () => queue,
  };
}

export type Garage = ReturnType<typeof createGarage>;
